import { unwrap } from '@mrigl/api-client';
import { Alert, Button, TextField } from '@mrigl/ui';
import { useState, type SubmitEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../api.js';
import { AuthLayout, consumeReturnTo } from '../components.js';
import { useI18n } from '../i18n.js';
import { useSession } from '../session.js';

/** Second step of sign-in for users with an authenticator (or a recovery code). */
export function MfaVerifyPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [mode, setMode] = useState<'app' | 'recovery'>('app');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'app') {
        await unwrap(api.POST('/admin-api/v1/auth/mfa/totp', { body: { code: code.trim() } }));
      } else {
        await unwrap(api.POST('/admin-api/v1/auth/mfa/recovery', { body: { code: code.trim() } }));
      }
      await refresh();
      void navigate(consumeReturnTo(), { replace: true });
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <h1 className="mrigl-heading">{t('admin.mfa.verifyTitle')}</h1>
      {mode === 'app' ? <p className="mrigl-muted">{t('admin.mfa.verifyIntro')}</p> : null}
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form className="mrigl-stack" onSubmit={(event) => void submit(event)}>
        <TextField
          key={mode}
          label={mode === 'app' ? t('admin.mfa.code') : t('admin.mfa.recoveryCode')}
          name="code"
          autoComplete="one-time-code"
          inputMode={mode === 'app' ? 'numeric' : 'text'}
          required
          ltr
          className="mrigl-code"
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
        />
        <Button type="submit" busy={busy}>
          {t('admin.mfa.verify')}
        </Button>
      </form>
      <Button
        variant="link"
        onClick={() => {
          setMode(mode === 'app' ? 'recovery' : 'app');
          setCode('');
          setError(null);
        }}
      >
        {mode === 'app' ? t('admin.mfa.useRecovery') : t('admin.mfa.useApp')}
      </Button>
    </AuthLayout>
  );
}
